import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const BASE_MS_PER_WORD = 280;

function formatPlaybackRate(rate) {
  if (!Number.isFinite(rate)) return '1';
  return rate.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Build a list of render tokens from the audio manifest's per-word
 * timing metadata. We slice the original `text` using `charStart` /
 * `charEnd` so spacing and punctuation are preserved exactly as
 * authored. If the metadata is missing offsets (or the offsets don't
 * line up with the rendered text), we fall back to the bare word text
 * separated by single spaces.
 */
function buildTimingTokens(text, wordTimings) {
  if (!Array.isArray(wordTimings) || wordTimings.length === 0) return null;

  const fullText = String(text || '');
  const tokens = [];
  let cursor = 0;

  for (let i = 0; i < wordTimings.length; i += 1) {
    const word = wordTimings[i];
    if (!word) continue;
    const charStart = Number.isFinite(word.charStart) ? word.charStart : null;
    const charEnd = Number.isFinite(word.charEnd) ? word.charEnd + 1 : null;

    let leading = '';
    let body;
    if (
      charStart != null &&
      charEnd != null &&
      charStart >= cursor &&
      charEnd >= charStart &&
      charEnd <= fullText.length
    ) {
      leading = fullText.slice(cursor, charStart);
      body = fullText.slice(charStart, charEnd);
      cursor = charEnd;
    } else {
      body = String(word.text || '');
      leading = i === 0 ? '' : ' ';
    }

    tokens.push({
      leading,
      body,
      startMs: Number.isFinite(word.startMs) ? word.startMs : 0,
      endMs: Number.isFinite(word.endMs) ? word.endMs : 0,
    });
  }

  const trailing = fullText.slice(cursor);
  return { tokens, trailing };
}

const LCVTutorDialog = ({
  text,
  label,
  animated = false,
  className,
  progressRatio,
  wordTimings = null,
  currentTimeMs = 0,
  onFinished,
  fontSize: externalFontSize,
  showControls = true,
  paused = false,
  onFontSizeChange,
  onRestart,
  onPause,
  onResume,
  canPause = false,
  onSkip,
  canSkip = false,
  canReplay = false,
  playbackRate = 1,
  canIncreasePlaybackRate = false,
  canDecreasePlaybackRate = false,
  onIncreasePlaybackRate,
  onDecreasePlaybackRate,
  volume = 100,
  canIncreaseVolume = false,
  canDecreaseVolume = false,
  onIncreaseVolume,
  onDecreaseVolume,
  isMuted = false,
  onToggleMute,
  onProgress,
  playbackFinished = false,
}) => {
  const [internalFontSize, setInternalFontSize] = useState(17);
  const [wordIndex, setWordIndex] = useState(0);

  const fontSize = externalFontSize ?? internalFontSize;
  const handleFontSizeChange = onFontSizeChange ?? setInternalFontSize;
  const hasExternalProgress =
    typeof progressRatio === 'number' && Number.isFinite(progressRatio);
  const clampedProgress = hasExternalProgress
    ? Math.max(0, Math.min(1, progressRatio))
    : null;

  // When the lesson-audio manifest gives us per-word timing metadata,
  // drive subtitle highlighting from `currentTimeMs` against each
  // word's `[startMs, endMs)` window — much tighter than the
  // duration-based heuristic used as a fallback.
  const timingTokens = useMemo(
    () => buildTimingTokens(text, wordTimings),
    [text, wordTimings],
  );
  const hasTimingMetadata = Boolean(timingTokens && timingTokens.tokens.length);

  const activeTimingWordIndex = useMemo(() => {
    if (!hasTimingMetadata) return -1;
    if (!Number.isFinite(currentTimeMs) || currentTimeMs <= 0) return -1;
    let lastIdx = -1;
    const tokenList = timingTokens.tokens;
    for (let i = 0; i < tokenList.length; i += 1) {
      if (currentTimeMs >= tokenList[i].startMs) {
        lastIdx = i;
      } else {
        break;
      }
    }
    return lastIdx;
  }, [hasTimingMetadata, timingTokens, currentTimeMs]);

  const words = useMemo(
    () => String(text || '').trim().split(/\s+/).filter(Boolean),
    [text],
  );
  const finishedRef = useRef(false);
  const containerRef = useRef(null);
  const activeWordRef = useRef(null);

  useEffect(() => {
    setWordIndex(0);
    finishedRef.current = false;
  }, [text]);

  useEffect(() => {
    if (hasTimingMetadata) {
      onProgress?.({
        spokenWords: Math.max(0, activeTimingWordIndex + 1),
        totalWords: timingTokens.tokens.length,
      });
      return;
    }
    onProgress?.({
      spokenWords: Math.min(
        hasExternalProgress
          ? Math.floor(clampedProgress * words.length)
          : wordIndex,
        words.length,
      ),
      totalWords: words.length,
    });
  }, [
    activeTimingWordIndex,
    clampedProgress,
    hasExternalProgress,
    hasTimingMetadata,
    onProgress,
    timingTokens,
    wordIndex,
    words.length,
  ]);

  useEffect(() => {
    if (
      !animated ||
      paused ||
      words.length === 0 ||
      hasExternalProgress ||
      hasTimingMetadata
    )
      return undefined;
    if (wordIndex >= words.length) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
      return undefined;
    }

    const timer = window.setTimeout(
      () => setWordIndex(current => current + 1),
      BASE_MS_PER_WORD,
    );

    return () => window.clearTimeout(timer);
  }, [
    animated,
    hasExternalProgress,
    hasTimingMetadata,
    onFinished,
    paused,
    wordIndex,
    words.length,
  ]);

  useEffect(() => {
    if (!animated || !hasExternalProgress) return undefined;
    if (clampedProgress >= 1) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        onFinished?.();
      }
      return undefined;
    }

    finishedRef.current = false;
    return undefined;
  }, [animated, clampedProgress, hasExternalProgress, onFinished]);

  const spokenWordCount = useMemo(() => {
    if (!animated) return words.length;
    if (hasExternalProgress) {
      return Math.min(
        words.length,
        Math.max(
          0,
          clampedProgress > 0 ? Math.ceil(clampedProgress * words.length) : 0,
        ),
      );
    }

    return Math.min(wordIndex, words.length);
  }, [animated, clampedProgress, hasExternalProgress, wordIndex, words.length]);

  useEffect(() => {
    if (!activeWordRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const activeWord = activeWordRef.current;
    const wordBottom = activeWord.offsetTop + activeWord.offsetHeight;
    const visibleBottom = container.scrollTop + container.clientHeight;

    if (wordBottom > visibleBottom - 4) {
      container.scrollTop = wordBottom - container.clientHeight + 8;
    }
  }, [wordIndex, activeTimingWordIndex]);

  const handleRestart = useCallback(() => {
    if (onRestart) {
      onRestart();
      return;
    }

    setWordIndex(0);
    finishedRef.current = false;
  }, [onRestart]);

  return (
    <div className={['lcv-tutor-dialog', className].filter(Boolean).join(' ')}>
      <div className="lcv-tutor-dialog-header">
        <div className="lcv-tutor-avatar-img" aria-hidden="true">
          🎩
        </div>
        <div className="lcv-tutor-name">Benjamin Franklin</div>
        {label && <span className="lcv-tutor-phase-label">{label}</span>}
      </div>

      <div
        ref={containerRef}
        className="lcv-tutor-dialog-text"
        style={{ fontSize: `${fontSize}px` }}
      >
        {animated && hasTimingMetadata && (
          <>
            {timingTokens.tokens.map((token, index) => {
              const isActive = index === activeTimingWordIndex && !playbackFinished;
              const isSpoken = index <= activeTimingWordIndex;
              const className = isActive
                ? 'lcv-tw-active'
                : isSpoken
                  ? 'lcv-tw-spoken'
                  : 'lcv-tw-pending';
              return (
                <React.Fragment key={`tw-${index}`}>
                  {token.leading}
                  <span
                    ref={isActive ? activeWordRef : undefined}
                    className={className}
                  >
                    {token.body}
                  </span>
                </React.Fragment>
              );
            })}
            {timingTokens.trailing}
          </>
        )}
        {animated && !hasTimingMetadata && (
          words.map((word, index) => (
            <span
              key={`${word}-${index}`}
              ref={index === spokenWordCount - 1 ? activeWordRef : undefined}
              className={
                index < spokenWordCount ? 'lcv-tw-spoken' : 'lcv-tw-pending'
              }
            >
              {word}{' '}
            </span>
          ))
        )}
        {!animated && text}
      </div>

      {showControls && (
        <div className="lcv-tutor-toolbar">
          <div className="lcv-tutor-tool-group">
            <span className="lcv-tutor-tool-label">Text</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize <= 14}
              onClick={() => handleFontSizeChange(fontSize - 1)}
            >
              A-
            </button>
            <span className="lcv-tutor-tool-value">{fontSize}px</span>
            <button
              type="button"
              className="lcv-tutor-tool-btn"
              disabled={fontSize >= 24}
              onClick={() => handleFontSizeChange(fontSize + 1)}
            >
              A+
            </button>
          </div>

          {(canSkip || canPause || canReplay || onIncreasePlaybackRate || onDecreasePlaybackRate || onIncreaseVolume || onDecreaseVolume) && (
            <div className="lcv-tutor-tool-actions">
              <div className="lcv-tutor-tool-action-row">
                {canReplay && (
                  <button
                    type="button"
                    className="lcv-tutor-tool-toggle"
                    onClick={handleRestart}
                  >
                    Play Again
                  </button>
                )}

                {canSkip && (
                  <button
                    type="button"
                    className="lcv-tutor-tool-toggle"
                    onClick={onSkip}
                  >
                    Skip
                  </button>
                )}

                {canPause && (
                  <button
                    type="button"
                    className="lcv-tutor-tool-toggle"
                    onClick={paused ? onResume : onPause}
                  >
                    {paused ? 'Continue' : 'Pause'}
                  </button>
                )}
              </div>

              {(onIncreasePlaybackRate ||
                onDecreasePlaybackRate ||
                onIncreaseVolume ||
                onDecreaseVolume) && (
                  <div className="lcv-tutor-audio-controls-row">
                    {(onIncreasePlaybackRate || onDecreasePlaybackRate) && (
                      <div className="lcv-tutor-speed-controls" aria-label="Playback speed controls">
                        <span className="lcv-tutor-control-prefix">Speed</span>
                        <button
                          type="button"
                          className="lcv-tutor-tool-btn"
                          onClick={onDecreasePlaybackRate}
                          disabled={!canDecreasePlaybackRate}
                          aria-label="Decrease playback speed"
                        >
                          -
                        </button>
                        <span className="lcv-tutor-speed-value">{`${formatPlaybackRate(playbackRate)}x`}</span>
                        <button
                          type="button"
                          className="lcv-tutor-tool-btn"
                          onClick={onIncreasePlaybackRate}
                          disabled={!canIncreasePlaybackRate}
                          aria-label="Increase playback speed"
                        >
                          +
                        </button>
                      </div>
                    )}

                    {(onIncreaseVolume || onDecreaseVolume) && (
                      <div className="lcv-tutor-speed-controls" aria-label="Playback volume controls">
                        <span className="lcv-tutor-control-prefix">Volume</span>
                        <button
                          type="button"
                          className="lcv-tutor-tool-btn"
                          onClick={onDecreaseVolume}
                          disabled={!canDecreaseVolume}
                          aria-label="Decrease playback volume"
                        >
                          -
                        </button>
                        <span className="lcv-tutor-speed-value">{`${volume}%`}</span>
                        <button
                          type="button"
                          className="lcv-tutor-tool-btn"
                          onClick={onIncreaseVolume}
                          disabled={!canIncreaseVolume}
                          aria-label="Increase playback volume"
                        >
                          +
                        </button>
                        {onToggleMute && (
                          <button
                            type="button"
                            className="lcv-tutor-mute-btn"
                            onClick={onToggleMute}
                            aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                            title={isMuted ? 'Unmute' : 'Mute'}
                          >
                            {isMuted ? '🔇' : '🔊'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}

          {/* {animated && (
            <button
              type="button"
              className="lcv-tutor-tool-restart"
              onClick={handleRestart}
            >
              Watch Again
            </button>
          )} */}
        </div>
      )}
    </div>
  );
};

export default LCVTutorDialog;
